import { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { SubsectionHeader } from '@/components/common';
import { FeedbackItem } from './FeedbackItem';
import type { StrengthFeedback, DevelopmentAreaFeedback, FeedbackItemRequest } from '@/api/types/jobEnd.types';

type FeedbackData = StrengthFeedback | DevelopmentAreaFeedback;

interface FeedbackItemListProps {
  title: string;
  items: FeedbackData[];
  canEdit: boolean;
  onSave: (items: FeedbackItemRequest[]) => void;
  minItems?: number;
  addButtonLabel: string;
  placeholder?: string;
  error?: boolean;
}

function createEmptyItem(orderIndex: number): FeedbackItemRequest {
  return {
    id: undefined,
    content: '',
    orderIndex,
  };
}

function ensureMinItems(items: FeedbackData[], minItems: number): FeedbackItemRequest[] {
  const result: FeedbackItemRequest[] = items.map((item) => ({
    id: item.id,
    content: item.content,
    orderIndex: item.orderIndex,
  }));

  while (result.length < minItems) {
    result.push(createEmptyItem(result.length));
  }

  return result;
}

export function FeedbackItemList({
  title,
  items,
  canEdit,
  onSave,
  minItems = 3,
  addButtonLabel,
  placeholder,
  error,
}: FeedbackItemListProps) {
  const hasContent = (content: string): boolean => {
    const stripped = content.replace(/<[^>]*>/g, '').trim();
    return stripped !== '';
  };

  const [localItems, setLocalItems] = useState<FeedbackItemRequest[]>(() =>
    ensureMinItems(items, minItems)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const localItemsRef = useRef(localItems);

  useEffect(() => {
    localItemsRef.current = localItems;
  }, [localItems]);

  useEffect(() => {
    setLocalItems(ensureMinItems(items, minItems));
  }, [items, minItems]);

  const handleItemChange = (index: number, content: string) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], content };
    setLocalItems(newItems);
  };

  const handleContainerBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    if (localItemsRef.current.length < minItems) return;
    onSave(localItemsRef.current);
  };

  const handleAddItem = () => {
    const newItems = [...localItems, createEmptyItem(localItems.length)];
    setLocalItems(newItems);
    onSave(newItems);
  };

  const handleDeleteItem = (index: number) => {
    if (localItems.length <= minItems) return;

    const newItems = localItems
      .filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, orderIndex: i }));

    setLocalItems(newItems);
    onSave(newItems);
  };

  const canDelete = localItems.length > minItems;

  const itemsToDisplay = canEdit
    ? localItems
    : localItems.filter((item) => hasContent(item.content));

  const showEmptyMessage = !canEdit && itemsToDisplay.length === 0;

  return (
    <Box ref={containerRef} onBlur={handleContainerBlur} sx={{ mb: 3 }}>
      <SubsectionHeader>{title}</SubsectionHeader>

      {showEmptyMessage ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No feedback
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {itemsToDisplay.map((item, index) => (
            <FeedbackItem
              key={item.id ?? `new-${index}`}
              content={item.content}
              onChange={(content) => handleItemChange(index, content)}
              onDelete={() => handleDeleteItem(index)}
              canEdit={canEdit}
              canDelete={canDelete}
              placeholder={placeholder}
              error={error && !hasContent(item.content)}
            />
          ))}
        </Box>
      )}

      {canEdit && (
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 2 }}
        >
          {addButtonLabel}
        </Button>
      )}
    </Box>
  );
}

export default FeedbackItemList;
